import { HttpClient } from '@angular/common/http';
import { TranslateLoader, TranslationObject } from '@ngx-translate/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Custom TranslateLoader that merges regional language variants with their base translations.
 * Regional variants (e.g. en_GB, en_AU, en_CA, de_DE, fr_FR) only contain override keys.
 * The loader detects them by prefix, loads the base locale, and deep-merges overrides on top.
 *
 * Adding a new regional variant only requires creating the override JSON file — no code changes.
 *
 * NOTE: kept in sync with smart-widget / dashboard / widget / booking-page (JB-484). The only
 * project-specific value is BASE_LOCALES — registration ships 4 base languages, not the full 9.
 */
export class MergeTranslateLoader implements TranslateLoader {
  private readonly BASE_LOCALES = [
    'de_CH', 'en_US', 'fr_CH', 'it_CH',
  ];

  constructor(
    private http: HttpClient,
    private prefix: string = './assets/i18n/',
    private suffix: string = '.json'
  ) {}

  getTranslation(lang: string): Observable<TranslationObject> {
    const normalized = this.normalizeLocale(lang);
    const baseLocale = this.getBaseLocale(normalized);

    if (baseLocale) {
      return this.loadMergedTranslations(baseLocale, normalized);
    }

    return this.http.get<TranslationObject>(`${this.prefix}${lang}${this.suffix}`);
  }

  /**
   * Normalizes locale codes to correct format (e.g. en_au → en_AU, en-gb → en_GB)
   */
  private normalizeLocale(lang: string): string {
    if (!lang || lang.length !== 5) return lang;
    const separator = lang[2];
    if (separator === '_' || separator === '-') {
      return `${lang.substring(0, 2).toLowerCase()}_${lang.substring(3).toUpperCase()}`;
    }
    return lang;
  }

  /**
   * Returns the base locale for a regional variant, or null if the locale IS a base locale.
   * e.g. en_GB → en_US, de_DE → de_CH, fr_FR → fr_CH
   */
  private getBaseLocale(locale: string): string | null {
    if (this.BASE_LOCALES.includes(locale)) return null;
    const langPrefix = locale.substring(0, 2);
    return this.BASE_LOCALES.find(base => base.startsWith(langPrefix + '_')) ?? null;
  }

  /**
   * Loads base locale translations and merges regional overrides on top
   */
  private loadMergedTranslations(baseLocale: string, regionalLocale: string): Observable<TranslationObject> {
    const baseUrl = `${this.prefix}${baseLocale}${this.suffix}`;
    const overrideUrl = `${this.prefix}${regionalLocale}${this.suffix}`;

    return forkJoin([
      this.http.get<TranslationObject>(baseUrl),
      this.http.get<TranslationObject>(overrideUrl).pipe(
        catchError(() => of({} as TranslationObject))
      )
    ]).pipe(
      map(([base, overrides]) => this.deepMerge(base, overrides))
    );
  }

  /**
   * Deep merges two objects, with source properties overriding target
   */
  private deepMerge(target: TranslationObject, source: TranslationObject): TranslationObject {
    const result = { ...target };

    for (const key of Object.keys(source)) {
      const sourceValue = source[key];
      const targetValue = result[key];

      if (this.isObject(sourceValue) && this.isObject(targetValue)) {
        result[key] = this.deepMerge(targetValue, sourceValue);
      } else {
        result[key] = sourceValue;
      }
    }

    return result;
  }

  /**
   * Type guard to check if a value is a plain object
   */
  private isObject(item: unknown): item is TranslationObject {
    return item !== null && typeof item === 'object' && !Array.isArray(item);
  }
}
